import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Textarea,
  Text,
  VStack,
  HStack,
  useToast,
} from "@chakra-ui/react";
import services from "../services";
import { extractErrorMessage } from "../utils/orderItemsActionUtils";

interface VoidOrderInvoiceConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderNo: string;
  invoiceNo: string;
  invoiceType: "ORDER" | "DELIVERY";
  onVoidSuccess: () => void;
}

const bodyCopyProps = {
  fontSize: "lg" as const,
  lineHeight: "tall" as const,
  fontWeight: "normal" as const,
  color: "gray.800",
};

const VoidOrderInvoiceConfirmationDialog: React.FC<
  VoidOrderInvoiceConfirmationDialogProps
> = ({ isOpen, onClose, orderNo, invoiceNo, invoiceType, onVoidSuccess }) => {
  const cancelRef = React.useRef(null);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!reason.trim() || !invoiceNo) return;
    setIsLoading(true);
    try {
      await services.voidInvoice(
        orderNo,
        invoiceNo,
        invoiceType,
        reason.trim(),
      );
      toast({
        title: `Invoice ${invoiceNo} has been deleted successfully`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      setReason("");
      onClose();
      onVoidSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to delete invoice",
        description: extractErrorMessage(error),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason("");
      onClose();
    }
  };

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={handleClose}
      returnFocusOnClose={false}
      isCentered
      closeOnOverlayClick={!isLoading}
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          maxW="2xl"
          borderRadius="lg"
          boxShadow="xl"
          aria-label="Delete invoice confirmation"
        >
          <AlertDialogBody pt={6} pb={5} px={{ base: 5, md: 8 }}>
            <VStack align="stretch" spacing={4}>
              <Text {...bodyCopyProps} fontWeight="semibold" color="gray.900">
                Are you sure you want to delete this invoice?
              </Text>
              <Text {...bodyCopyProps}>
                Note: It will be removed from this order and marked as void in
                Zoho Books.
              </Text>
              <VStack align="stretch" spacing={3}>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Invoice Type
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {invoiceType}
                  </Text>
                </Text>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Order Number
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {orderNo}
                  </Text>
                </Text>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Invoice Number
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {invoiceNo}
                  </Text>
                </Text>
              </VStack>
              <Textarea
                placeholder="Enter reason for deleting this invoice"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={256}
                isDisabled={isLoading}
                fontSize="lg"
                size="lg"
                rows={3}
              />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter
            flexDirection="column"
            alignItems="stretch"
            gap={0}
            pt={2}
            pb={6}
            px={{ base: 5, md: 8 }}
          >
            <HStack justify="flex-end" spacing={3} w="100%">
              <Button
                ref={cancelRef}
                onClick={handleClose}
                isDisabled={isLoading}
                size="md"
                fontSize="lg"
                py={2}
                px={4}
                minH="auto"
                h="auto"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                bg="red.500"
                onClick={handleSubmit}
                isDisabled={!reason.trim() || isLoading}
                isLoading={isLoading}
                loadingText="Deleting…"
                size="md"
                fontSize="lg"
                py={2}
                px={4}
                minH="auto"
                h="auto"
              >
                Delete Invoice
              </Button>
            </HStack>
            {isLoading && (
              <Text
                textAlign="center"
                fontSize="sm"
                lineHeight="short"
                color="gray.600"
                pt={4}
                px={2}
              >
                Please wait while we perform the action. Don&apos;t close the
                window.
              </Text>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default VoidOrderInvoiceConfirmationDialog;
